import { useState, useEffect } from "react";
import styles from "../Community/CreateCommunityPage.module.css";
import api from "../../services/api";
import { ENDPOINTS } from "../../utils/endpoints";
import { toast } from "react-toastify";
import CommunityContent from "../../components/Community/CommunityContent";
import VideoTabsManager from "../../components/VideoTabsManager";
import { useVideoTabs } from "../../hooks/useVideoTabs";
import useDashboardStore from "../../store/dashboardStore";

const EditWeeklyLinksContentPage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedCommunityId, setSelectedCommunityId] = useState(null);
  const { communities } = useDashboardStore();

  const [content, setContent] = useState({
    communityTitle: "",
    contentTitle: "",
    primaryText: "",
    secondaryText: "",
    contentBannerWebFile: null,
    contentBannerWebUrl: "",
    contentBannerAppFile: null,
    contentBannerAppUrl: "",
    contentImageWebFile: null,
    contentImageWebUrl: "",
    contentImageAppFile: null,
    contentImageAppUrl: "",
    deleteContentBannerWeb: false,
    deleteContentBannerApp: false,
    deleteContentImageWeb: false,
    deleteContentImageApp: false,
  });

  const videoTabsState = useVideoTabs("weekly-links");

  useEffect(() => {
    if (!selectedCommunityId) return;

    const loadContent = async () => {
      try {
        const { data } = await api.get(
          ENDPOINTS.GET_PAGE_CONTENT(selectedCommunityId, "weeklylinks"),
        );

        if (data.success && data.content) {
          const c = data.content;

          setContent({
            communityTitle: c.academy_title || "",
            contentTitle: c.content_title || "",
            primaryText: c.content_primary_text || "",
            secondaryText: c.content_secondary_text || "",
            contentBannerWebUrl: c.content_banner_web
              ? ENDPOINTS.IMAGE_BASE_URL + c.content_banner_web
              : null,
            contentBannerAppUrl: c.content_banner_app
              ? ENDPOINTS.IMAGE_BASE_URL + c.content_banner_app
              : null,
            contentImageWebUrl: c.content_image_web
              ? ENDPOINTS.IMAGE_BASE_URL + c.content_image_web
              : null,
            contentImageAppUrl: c.content_image_app
              ? ENDPOINTS.IMAGE_BASE_URL + c.content_image_app
              : null,
            contentBannerWebFile: null,
            contentBannerAppFile: null,
            contentImageWebFile: null,
            contentImageAppFile: null,
            deleteContentBannerWeb: false,
            deleteContentBannerApp: false,
            deleteContentImageWeb: false,
            deleteContentImageApp: false,
          });

          // Prefer new key, fallback to old for backward compat
          const tabs = c.video_tabs || c.video_links || [];
          const loadedTabs = tabs.map((tab) => ({
            ...tab,
            groups: (tab.groups || []).map((group) => ({
              ...group,
              group_banner_mobile_url: group.group_banner_mobile_url
                ? ENDPOINTS.IMAGE_BASE_URL + group.group_banner_mobile_url
                : null,
              group_banner_web_url: group.group_banner_web_url
                ? ENDPOINTS.IMAGE_BASE_URL + group.group_banner_web_url
                : null,
              videos: (group.videos || []).map((video) => ({
                ...video,
                cover_image: video.cover_image
                  ? ENDPOINTS.IMAGE_BASE_URL + video.cover_image
                  : null,
                attachment_file: null,
                attachment_file_url: video.attach_file
                  ? ENDPOINTS.IMAGE_BASE_URL + video.attach_file
                  : null,
                membership_expiry: video.membership_expiry || [],
              })),
            })),
          }));
          videoTabsState.setVideoTabs(loadedTabs);
        }
      } catch (err) {
        console.error("Failed to load weekly links content", err);
        toast.error("Failed to load weekly links content");
      }
    };

    loadContent();
  }, [selectedCommunityId]);

  const handleSubmit = async () => {
    if (isSubmitting) return;
    if (!selectedCommunityId) {
      toast.error("Please select a community first");
      return;
    }

    setIsSubmitting(true);

    try {
      const formData = new FormData();

      formData.append("page_type", "weeklylinks");
      formData.append("community_id", selectedCommunityId);

      if (content.communityTitle)
        formData.append("academy_title", content.communityTitle);
      if (content.contentTitle)
        formData.append("content_title", content.contentTitle);
      if (content.primaryText)
        formData.append("content_primary_text", content.primaryText);
      if (content.secondaryText)
        formData.append("content_secondary_text", content.secondaryText);

      if (content.deleteContentBannerWeb)
        formData.append("delete_content_banner_web", "true");
      if (content.deleteContentBannerApp)
        formData.append("delete_content_banner_app", "true");
      if (content.deleteContentImageWeb)
        formData.append("delete_content_image_web", "true");
      if (content.deleteContentImageApp)
        formData.append("delete_content_image_app", "true");

      if (content.contentBannerWebFile)
        formData.append("content_banner_web", content.contentBannerWebFile);
      if (content.contentBannerAppFile)
        formData.append("content_banner_app", content.contentBannerAppFile);
      if (content.contentImageWebFile)
        formData.append("content_image_web", content.contentImageWebFile);
      if (content.contentImageAppFile)
        formData.append("content_image_app", content.contentImageAppFile);

      videoTabsState.videoCoverFiles.forEach((file) =>
        formData.append("video_covers", file),
      );

      let attachments = [];
      let groupBanners = [];
      (videoTabsState?.videoTabs || []).forEach((tab) => {
        (tab?.groups || []).forEach((group) => {
          if (group?.group_banner_mobile_file) {
            groupBanners.push(group.group_banner_mobile_file);
          }
          if (group?.group_banner_web_file) {
            groupBanners.push(group.group_banner_web_file);
          }

          (group?.videos || []).forEach((video) => {
            if (video?.attachment_file) attachments.push(video.attachment_file);
          });
        });
      });
      attachments.forEach((file) => formData.append("video_attachments", file));

// Append all group banner files
      groupBanners.forEach((file) => {
        formData.append("group_files", file);
      });

      // Video tabs structure
      if (videoTabsState.videoTabs.length > 0) {
        const sortedTabs = videoTabsState.videoTabs.map((tab, tabIdx) => ({
          ...tab,
          sort_order: tabIdx,
groups: (tab.groups || []).map((group, groupIdx) => {
            // Mobile
            let cleanGroupMobileUrl = group.group_banner_mobile_url;
            if (cleanGroupMobileUrl && cleanGroupMobileUrl.includes("/uploads/")) {
              const parts = cleanGroupMobileUrl.split("/uploads/");
              cleanGroupMobileUrl = parts[parts.length - 1];
            }

            let group_banner_mobile_index = null;
            if (group.group_banner_mobile_file) {
              const index = groupBanners.findIndex(
                (f) => f?.name === group.group_banner_mobile_file?.name &&
                  f?.size === group.group_banner_mobile_file?.size
              );
              group_banner_mobile_index = index !== -1 ? index : null;
            }

            // Web
            let cleanGroupWebUrl = group.group_banner_web_url;
            if (cleanGroupWebUrl && cleanGroupWebUrl.includes("/uploads/")) {
              const parts = cleanGroupWebUrl.split("/uploads/");
              cleanGroupWebUrl = parts[parts.length - 1];
            }

            let group_banner_web_index = null;
            if (group.group_banner_web_file) {
              const index = groupBanners.findIndex(
                (f) => f?.name === group.group_banner_web_file?.name &&
                  f?.size === group.group_banner_web_file?.size
              );
              group_banner_web_index = index !== -1 ? index : null;
            }

            return {
              ...group,
              sort_order: groupIdx,
              group_banner_mobile_url: cleanGroupMobileUrl,
              group_banner_mobile_index: group_banner_mobile_index,
              group_banner_web_url: cleanGroupWebUrl,
              group_banner_web_index: group_banner_web_index,
              videos: (group.videos || []).map((video, videoIdx) => {
                let cleanCoverImage = video.cover_image;
                if (cleanCoverImage && cleanCoverImage.includes("/uploads/")) {
                  const parts = cleanCoverImage.split("/uploads/");
                  cleanCoverImage = parts[parts.length - 1];
                }

                let cleanAttachedFileUrl = video.attachment_file_url;
                if (cleanAttachedFileUrl && cleanAttachedFileUrl.includes("/uploads/")) {
                  const parts = cleanAttachedFileUrl.split("/uploads/");
                  cleanAttachedFileUrl = parts[parts.length - 1];
                }

                let attach_file_index = null;
                if (video.attachment_file) {
                  const index = attachments.findIndex(
                    (f) => f?.name === video.attachment_file?.name &&
                      f?.size === video.attachment_file?.size
                  );
                  attach_file_index = index !== -1 ? index : null;
                }

                return {
                  id: video.id ?? null,
                  title: video.title,
                  url: video.url,
                  video_type: video.video_type,
                  membership_ids: video.membership_ids || [],
                  sort_order: videoIdx,
                  cover_image: cleanCoverImage,
                  cover_image_index: video.cover_image_index,
                  membership_expiry: video.membership_expiry || [],
                  attach_file: cleanAttachedFileUrl,
                  attach_file_index,
                  ...(video.delete_cover_image ? { delete_cover_image: true } : {}),
                };
              }),
            }
          }),
        }));

        formData.append("video_tabs", JSON.stringify(sortedTabs));
      }
      const { data } = await api.put(ENDPOINTS.UPDATE_PAGE_CONTENT, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (data.success) {
        toast.success(
          data.message || "Weekly Links content updated successfully",
        );

        // Reload fresh data so new video IDs are captured
        const { data: refreshed } = await api.get(
          ENDPOINTS.GET_PAGE_CONTENT(selectedCommunityId, "weeklylinks"),
        );
        if (refreshed.success && refreshed.content) {
          const c = refreshed.content;
          const tabs = c.video_tabs || c.video_links || [];
          const loadedTabs = tabs.map((tab) => ({
            ...tab,
            groups: (tab.groups || []).map((group) => ({
              ...group,
              videos: (group.videos || []).map((video) => ({
                ...video,
                cover_image: video.cover_image
                  ? ENDPOINTS.IMAGE_BASE_URL + video.cover_image
                  : null,
                attachment_file: null,
                attachment_file_url: video.attach_file
                  ? ENDPOINTS.IMAGE_BASE_URL + video.attach_file
                  : null,
                membership_expiry: video.membership_expiry || [],
              })),
            })),
          }));
          videoTabsState.setVideoTabs(loadedTabs);
          videoTabsState.setVideoCoverFiles([]);
        }
      } else {
        toast.error(data.message || "Something went wrong");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error updating weekly links content");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.appbarContainer}>
        <h2 className={styles.appbarTitle}>Edit Weekly Links Content</h2>
      </div>

      <div className={styles.bodyContainer}>
        <div className={styles.rightSection}>
          {/* Community Selector */}
          <div className={styles.sectionsCard}>
            <div className={styles.rowInputGroup}>
              <label className={styles.inlineLabel}>Select Community *</label>
              <select
                className={styles.textInput}
                value={selectedCommunityId || ""}
                onChange={(e) => {
                  setSelectedCommunityId(
                    e.target.value ? Number(e.target.value) : null,
                  );
                  setContent({
                    communityTitle: "",
                    contentTitle: "",
                    primaryText: "",
                    secondaryText: "",
                    contentBannerWebUrl: null,
                    contentBannerAppUrl: null,
                    contentImageWebUrl: null,
                    contentImageAppUrl: null,
                    contentBannerWebFile: null,
                    contentBannerAppFile: null,
                    contentImageWebFile: null,
                    contentImageAppFile: null,
                    deleteContentBannerWeb: false,
                    deleteContentBannerApp: false,
                    deleteContentImageWeb: false,
                    deleteContentImageApp: false,
                  });
                  videoTabsState.setVideoTabs([]);
                }}
                disabled={isSubmitting}
              >
                <option value="">-- Select a Community --</option>
                {communities.map((community) => (
                  <option key={community.id} value={community.id}>
                    {community.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {selectedCommunityId && (
            <>
              <CommunityContent
                content={content}
                setContent={setContent}
                isSubmitting={isSubmitting}
                title={"Content Title"}
              />

              <VideoTabsManager
                videoTabsState={videoTabsState}
                isSubmitting={isSubmitting}
              />

              <button
                className={`${styles.submitButton} ${isSubmitting ? styles.submitting : ""}`}
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span>Updating...</span>
                    <span className={styles.submitIcon}>⟳</span>
                  </>
                ) : (
                  <>
                    <span>Update Content</span>
                    <span className={styles.submitIcon}>✓</span>
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default EditWeeklyLinksContentPage;
